import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Globe,
  Link,
  ExternalLink,
  CheckCircle,
  XCircle,
  RefreshCw,
  Lock,
  Crown,
  Save,
  Loader2,
  Plus,
  Trash2,
  Copy,
  Check,
  AlertCircle,
  Info,
  ArrowRight,
  Shield,
  Eye,
  ChevronRight,
  Users,
  Settings,
  MoreVertical,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import ConnectDomainModal from './ConnectDomainModal';
import FundGeniusDomainsSection from './FundGeniusDomainsSection';
import { isFundGeniusAgency } from '../../../utils/agencyType';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Domain Status States
const DOMAIN_STATUS = {
  NOT_CONNECTED: { label: 'Not Connected', color: 'slate', icon: XCircle },
  DNS_PENDING: { label: 'DNS Pending', color: 'amber', icon: RefreshCw },
  VERIFICATION_PENDING: { label: 'Verifying', color: 'blue', icon: RefreshCw },
  SSL_PROVISIONING: { label: 'SSL Provisioning', color: 'purple', icon: Shield },
  ACTIVE: { label: 'Active', color: 'emerald', icon: CheckCircle },
  VERIFICATION_FAILED: { label: 'Verification Failed', color: 'red', icon: XCircle },
  SSL_FAILED: { label: 'SSL Failed', color: 'red', icon: AlertCircle },
  DISCONNECTED: { label: 'Disconnected', color: 'slate', icon: XCircle },
};

// Domain destinations
const DESTINATIONS = {
  managed_client_portal: { name: 'Managed Client Portal', icon: Users, color: 'teal' },
  self_serve_portal: { name: 'Self-Serve / Auto Repair Portal', icon: Users, color: 'violet' },
  client_portal: { name: 'Client Portal', icon: Users, color: 'teal' }, // Legacy compatibility
  redirect: { name: 'Redirect', icon: ArrowRight, color: 'blue' },
};

/**
 * Domain Status Badge Component
 */
const DomainStatusBadge = ({ status }) => {
  const statusConfig = DOMAIN_STATUS[status] || DOMAIN_STATUS.NOT_CONNECTED;
  const Icon = statusConfig.icon;
  
  const colorMap = {
    slate: 'bg-slate-100 text-slate-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
  };
  
  return (
    <Badge className={`${colorMap[statusConfig.color]} border-0`}>
      <Icon size={12} className="mr-1" />
      {statusConfig.label}
    </Badge>
  );
};

/**
 * Connected Domain Row Component
 */
const ConnectedDomainRow = ({ domain, destination, status, isRedirect, sslStatus, onVerify, onRemove }) => {
  const DestIcon = isRedirect ? ArrowRight : (DESTINATIONS[destination]?.icon || Globe);
  const destColor = isRedirect ? 'blue' : (DESTINATIONS[destination]?.color || 'slate');
  
  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${destColor}-100`}>
          <DestIcon size={20} className={`text-${destColor}-600`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono font-semibold text-slate-900">{domain}</code>
            <a 
              href={`https://${domain}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-teal-600"
            >
              <ExternalLink size={14} />
            </a>
          </div>
          <p className="text-xs text-slate-500">
            {isRedirect ? 'Redirects to primary' : (DESTINATIONS[destination]?.name || 'Client Portal')}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        {/* SSL Status */}
        {status === 'ACTIVE' && (
          <div className="flex items-center gap-1 text-xs text-emerald-600">
            <Shield size={12} />
            <span>SSL</span>
          </div>
        )}
        
        <DomainStatusBadge status={status} />
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onVerify}>
              <RefreshCw size={14} className="mr-2" />
              Verify / Refresh
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onRemove} className="text-red-600">
              <Trash2 size={14} className="mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

/**
 * Empty State Component
 */
const EmptyState = ({ onConnect }) => (
  <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
    <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
      <Globe className="text-slate-400" size={32} />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Domains Connected</h3>
    <p className="text-slate-500 mb-6 max-w-sm mx-auto">
      Connect your custom domain to give your clients a branded portal experience.
    </p>
    <Button 
      onClick={onConnect}
      className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
      data-testid="connect-first-domain-btn"
    >
      <Plus size={16} className="mr-2" />
      Connect a Domain
    </Button>
  </div>
);

/**
 * Domains & Redirects Section
 * 
 * The official home for FundElite domain configuration.
 * Uses a guided "Connect a Domain" flow.
 * 
 * Supports:
 * - Primary Client Portal Domain (required)
 * - Optional Redirect Domain (redirect-only)
 * - Connected domains list with status
 * - Manual DNS Setup (default)
 * - Automatic DNS Setup (when supported)
 */
const DomainsRedirectsSection = ({ 
  agency, 
  agencyId, 
  entitlements, 
  isReadOnly, 
  onUpdate,
  internalOverrides = {},
  isInternalAgency = false,
  navigateToSection,
}) => {
  const { token } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  
  // Check entitlements
  const hasCustomerEntitlement = (entitlements?.custom_domain && entitlements.custom_domain !== 'hidden') ||
    (entitlements?.white_label && entitlements.white_label !== 'hidden');
  const hasInternalOverride = isInternalAgency && (
    internalOverrides?.white_label_full || 
    internalOverrides?.white_label_starter
  );
  const isEntitled = hasCustomerEntitlement || hasInternalOverride;
  const isLocked = entitlements?.custom_domain === 'locked' && !hasInternalOverride;
  
  // Check if Starter or Full White Label
  const isStarterWhiteLabel = !entitlements?.advanced_white_label || 
    entitlements?.advanced_white_label === 'hidden' ||
    entitlements?.advanced_white_label === 'locked';
  
  // Determine agency portal types (which portal experiences the agency offers)
  // This would typically come from agency configuration
  const agencyPortalTypes = {
    hasManagedPortal: agency?.portal_types?.managed_client_portal !== false,
    hasSelfServePortal: agency?.portal_types?.self_serve_portal !== false,
  };
  
  // If agency has no explicit portal_types set, check their service model
  // Default: both portal types available unless explicitly disabled
  if (!agency?.portal_types) {
    // Use service_model if available, otherwise default to both
    const serviceModel = agency?.service_model || 'both';
    agencyPortalTypes.hasManagedPortal = serviceModel === 'managed' || serviceModel === 'both';
    agencyPortalTypes.hasSelfServePortal = serviceModel === 'self_serve' || serviceModel === 'both';
  }
  
  // Domain data from agency
  const domains = agency?.domains || {};
  const primaryDomain = domains.primary_portal_domain || '';
  const redirectDomain = domains.redirect_domain || '';
  const primaryStatus = domains.primary_status || 'NOT_CONNECTED';
  const redirectStatus = domains.redirect_status || 'NOT_CONNECTED';
  const sslStatus = domains.ssl_status || 'pending';
  const brandedPortalType = domains.branded_portal_type || null; // Which portal is branded under Starter
  
  // Build connected domains list
  const connectedDomains = [];
  if (primaryDomain) {
    connectedDomains.push({
      domain: primaryDomain,
      destination: brandedPortalType || 'managed_client_portal', // Use actual branded portal type
      status: primaryStatus,
      isRedirect: false,
      sslStatus: sslStatus,
    });
  }
  if (redirectDomain) {
    connectedDomains.push({
      domain: redirectDomain,
      destination: brandedPortalType || 'managed_client_portal',
      status: redirectStatus,
      isRedirect: true,
      sslStatus: 'pending',
    });
  }
  
  const handleVerifyDomain = async (domain) => {
    setIsVerifying(true);
    try {
      const response = await axios.post(
        `${API}/admin/agencies/${agencyId}/domains/verify`,
        { 
          primary_domain: domain === primaryDomain ? domain : primaryDomain,
          redirect_domain: domain === redirectDomain ? domain : redirectDomain,
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      
      if (response.data.primary_verified) {
        toast.success('Domain verified successfully!');
      } else {
        toast.info('Verification in progress. DNS may still be propagating.');
      }
      
      if (onUpdate) {
        onUpdate({ domains: response.data });
      }
    } catch (err) {
      console.error('Verification failed:', err);
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };
  
  const handleRemoveDomain = async (domain) => {
    if (!window.confirm(`Are you sure you want to remove ${domain}?`)) return;
    
    try {
      const newDomains = { ...domains };
      if (domain === primaryDomain) {
        newDomains.primary_portal_domain = '';
        newDomains.primary_status = 'NOT_CONNECTED';
      }
      if (domain === redirectDomain) {
        newDomains.redirect_domain = '';
        newDomains.redirect_status = 'NOT_CONNECTED';
      }
      
      await axios.patch(
        `${API}/admin/agencies/${agencyId}/domains`,
        { domains: newDomains },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      
      toast.success('Domain removed');
      if (onUpdate) onUpdate({ domains: newDomains });
    } catch (err) {
      console.error('Failed to remove domain:', err);
      toast.error('Failed to remove domain');
    }
  };
  
  const handleConnectSuccess = (result) => {
    if (onUpdate) {
      onUpdate({ 
        domains: {
          primary_portal_domain: result.primary_domain,
          primary_status: result.primary_status,
          redirect_domain: result.redirect_domain,
          redirect_status: result.redirect_status,
          ssl_status: result.ssl_status,
        }
      });
    }
  };
  
  if (isFundGeniusAgency(agency)) {
    return (
      <FundGeniusDomainsSection
        agency={agency}
        agencyId={agencyId}
        isReadOnly={isReadOnly}
        onUpdate={onUpdate}
      />
    );
  }

  // Show locked state if not entitled
  if (!isEntitled) {
    return (
      <div className="space-y-6" data-testid="domains-section-locked">
        <div className="border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Globe className="text-teal-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Domains & Redirects</h2>
              <p className="text-sm text-slate-500">Configure custom domains for your client portal</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Lock className="text-slate-400" size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Custom Domains Not Available</h3>
          <p className="text-slate-500 mb-4 max-w-md mx-auto">
            Custom domain configuration is part of Starter White Label and requires a plan upgrade.
          </p>
          <Badge className="bg-amber-100 text-amber-700 border-0">
            <Crown size={12} className="mr-1" />
            Upgrade to Starter White Label
          </Badge>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="domains-section">
      {/* Internal Test Access Banner */}
      {hasInternalOverride && !hasCustomerEntitlement && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Eye className="text-violet-600" size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-violet-800">Internal Test Access</p>
            <p className="text-xs text-violet-600">
              Domain configuration available for QA/demo testing.
            </p>
          </div>
          <Badge className="bg-violet-100 text-violet-700 border-0 ml-auto">Internal Override</Badge>
        </div>
      )}
      
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Globe className="text-teal-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Domains & Redirects</h2>
              <p className="text-sm text-slate-500">
                Manage custom domains for your client portal
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-100 text-emerald-700 border-0">
              <CheckCircle size={12} className="mr-1" />
              {isStarterWhiteLabel ? 'Starter White Label' : 'Full White Label'}
            </Badge>
            
            {connectedDomains.length > 0 && (
              <Button 
                onClick={() => setShowConnectModal(true)}
                className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white"
                disabled={isReadOnly || isLocked}
                data-testid="connect-domain-btn"
              >
                <Plus size={16} className="mr-2" />
                Connect a Domain
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Starter White Label Info */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border border-teal-200 p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
            <Info className="text-teal-600" size={18} />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900 mb-1">
              {isStarterWhiteLabel ? 'Starter White Label' : 'Full White Label'} Domain Setup
            </h4>
            <p className="text-sm text-teal-800 mb-3">
              Connect your custom domain for your client-facing portal. Your clients will access their 
              dashboard at your branded domain.
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-teal-700">
              <div className="flex items-center gap-1">
                <CheckCircle size={12} />
                <span>Primary Portal Domain</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle size={12} />
                <span>Optional Redirect</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle size={12} />
                <span>Free SSL Certificate</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Connected Domains List or Empty State */}
      {connectedDomains.length === 0 ? (
        <EmptyState onConnect={() => setShowConnectModal(true)} />
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide px-1">
            Connected Domains
          </h3>
          {connectedDomains.map((d, idx) => (
            <ConnectedDomainRow
              key={idx}
              domain={d.domain}
              destination={d.destination}
              status={d.status}
              isRedirect={d.isRedirect}
              sslStatus={d.sslStatus}
              onVerify={() => handleVerifyDomain(d.domain)}
              onRemove={() => handleRemoveDomain(d.domain)}
            />
          ))}
        </div>
      )}
      
      {/* Navigate to Branding */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Eye className="text-purple-600" size={18} />
            </div>
            <div>
              <p className="font-medium text-slate-900">Configure Your Brand</p>
              <p className="text-sm text-slate-500">
                Set up logo, colors, and portal branding
              </p>
            </div>
          </div>
          <Button 
            variant="outline"
            onClick={() => navigateToSection && navigateToSection('branding')}
            data-testid="go-to-branding-btn"
          >
            <ChevronRight size={16} className="mr-1" />
            Branding & White Label
          </Button>
        </div>
      </div>
      
      {/* Connect Domain Modal */}
      <ConnectDomainModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        agencyId={agencyId}
        entitlements={entitlements}
        isStarterWhiteLabel={isStarterWhiteLabel}
        onSuccess={handleConnectSuccess}
        agencyPortalTypes={agencyPortalTypes}
        existingBrandedPortal={brandedPortalType}
      />
    </div>
  );
};

export default DomainsRedirectsSection;
